import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Clock, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts';

// DailyBonusBanner: small dismissible card the markets page mounts
// for logged-in players. Two states:
//
//  - claimable: "Tagesbonus +25 VC abholen" CTA-Button
//  - cooldown: shows time-until-next + last claim
//
// Status is fetched once on mount; after a claim we re-render with
// the new cooldown without another API hop.
const DailyBonusBanner = () => {
  // Tagesbonus ist eine Spieler-Funktion (Credits zum Wetten). Admins
  // sind Operatoren, keine Teilnehmer -> kein Banner, kein Status-Fetch.
  const { user, isAdmin, refreshCredits } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Live ticker so countdown updates every second.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000); // minute precision
    return () => clearInterval(t);
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!user || isAdmin) return;
    try {
      const res = await api.profile.getDailyBonusStatus();
      setStatus(res.data);
    } catch (e) {
      // Silent: kein Bonus = kein Drama. Banner verschwindet einfach.
      console.warn('daily-bonus status fetch failed', e);
    }
  }, [user, isAdmin]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!user || isAdmin || dismissed || !status) return null;

  const handleClaim = async () => {
    setLoading(true);
    try {
      const res = await api.profile.claimDailyBonus();
      toast.success(`+${res.data.amount} VC Tagesbonus abgeholt`);
      refreshCredits?.();
      // Status lokal aktualisieren statt Refetch-Roundtrip.
      const cooldownEnd = res.data.next_claim_at;
      setStatus({
        can_claim: false,
        amount: res.data.amount,
        last_claim_at: new Date().toISOString(),
        next_claim_at: cooldownEnd,
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Tagesbonus konnte nicht abgeholt werden.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (status.can_claim) {
    return (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-sky-50 dark:from-emerald-500/10 dark:to-emerald-500/5 dark:border-emerald-500/30 dark:backdrop-blur-sm p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Gift className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
              Tagesbonus verfügbar
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              Hol dir <span className="font-medium">+{status.amount} VC</span> für die heutige Session.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleClaim}
          disabled={loading}
          className="w-full sm:w-auto flex-shrink-0 bg-emerald-600 hover:bg-emerald-700"
        >
          <Sparkles className="h-4 w-4 mr-1" />
          {loading ? 'Lädt…' : 'Abholen'}
        </Button>
      </div>
    );
  }

  // Cooldown-State: nur dezent als Status-Pille, nicht Banner-Block.
  if (status.next_claim_at) {
    const next = new Date(status.next_claim_at).getTime();
    const diffMs = next - now;
    if (diffMs <= 0) {
      // Cooldown ist gerade abgelaufen — Status erneut holen und
      // dann wieder oben Claim-State zeigen.
      fetchStatus();
      return null;
    }
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const label = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
    return (
      <div className="mb-4 flex items-center justify-between gap-3 text-xs text-gray-500 px-1 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Nächster Tagesbonus in {label}
        </span>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
          aria-label="Hinweis ausblenden"
        >
          ×
        </button>
      </div>
    );
  }

  return null;
};

export default DailyBonusBanner;
